import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingCart, Zap } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { StarRating } from "@/components/StarRating";
import { formatINR } from "@/config";

const BADGE_STYLES = {
  bestseller: "bg-[#C2410C] text-white",
  "kashmir-special": "bg-[#15803D] text-white",
  deal: "bg-[#D97706] text-white",
  premium: "bg-[#1C120C] text-[#FDE68A]",
  combo: "bg-[#7C2D12] text-white",
  festive: "bg-[#9333EA] text-white",
  giftbox: "bg-[#B45309] text-white",
};
const BADGE_LABEL = {
  bestseller: "Best Seller",
  "kashmir-special": "Kashmir Special",
  deal: "Today's Deal",
  premium: "Premium",
  combo: "Combo",
  festive: "Festive",
  giftbox: "Gift Box",
};

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished, setCartOpen } = useStore();
  const defaultIdx = product.weights.length > 1 ? 1 : 0;
  const [wIdx, setWIdx] = useState(defaultIdx);
  const w = product.weights[wIdx];
  const off = w.mrp > w.price ? Math.round(((w.mrp - w.price) / w.mrp) * 100) : 0;
  const wished = isWished(product.slug);

  const badge = product.badges?.[0];

  return (
    <div
      className="group relative flex flex-col rounded-2xl bg-white border border-[#E7E0D6] overflow-hidden card-hover"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative">
        <div
          className="aspect-square overflow-hidden cursor-pointer bg-[#F3ECE2]"
          onClick={() => navigate(`/product/${product.slug}`)}
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        </div>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {badge && (
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${BADGE_STYLES[badge] || "bg-[#C2410C] text-white"}`}>
              {BADGE_LABEL[badge] || badge}
            </span>
          )}
          {off > 0 && (
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#9A3412]">
              Save {off}%
            </span>
          )}
        </div>
        <button
          onClick={() => toggleWishlist(product.slug)}
          data-testid={`wishlist-btn-${product.slug}`}
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/90 backdrop-blur border border-[#E7E0D6] hover:scale-110 transition-transform"
          aria-label="wishlist"
        >
          <Heart size={17} className={wished ? "fill-[#C2410C] text-[#C2410C]" : "text-[#6B5B52]"} />
        </button>
      </div>

      <div className="flex flex-col flex-1 p-4">
        <p className="text-[11px] uppercase tracking-widest text-[#C2410C] font-semibold">{product.origin}</p>
        <h3
          className="font-display text-base sm:text-lg font-semibold text-[#1F130B] leading-snug mt-1 cursor-pointer hover:text-[#C2410C] transition-colors line-clamp-2"
          onClick={() => navigate(`/product/${product.slug}`)}
        >
          {product.name}
        </h3>
        <div className="mt-1.5">
          <StarRating value={product.rating} count={product.reviews_count} />
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {product.weights.map((ww, i) => (
            <button
              key={ww.label}
              onClick={() => setWIdx(i)}
              data-testid={`weight-${product.slug}-${ww.label}`}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                i === wIdx
                  ? "bg-[#1C120C] text-white border-[#1C120C]"
                  : "bg-transparent text-[#6B5B52] border-[#E7E0D6] hover:border-[#C2410C]"
              }`}
            >
              {ww.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-end gap-2">
          <span className="text-xl font-bold text-[#1F130B]">{formatINR(w.price)}</span>
          {w.mrp > w.price && (
            <span className="text-sm text-[#9CA3AF] line-through mb-0.5">{formatINR(w.mrp)}</span>
          )}
        </div>

        <div className="mt-3 flex gap-2 mt-auto pt-3">
          <button
            onClick={() => {
              addToCart(product, w);
              setCartOpen(true);
            }}
            data-testid={`add-to-cart-${product.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold py-2.5 transition-colors"
          >
            <ShoppingCart size={15} /> Add
          </button>
          <button
            onClick={() => {
              addToCart(product, w, 1, true);
              navigate("/checkout");
            }}
            data-testid={`buy-now-${product.slug}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-[#D97706] text-[#9A3412] hover:bg-[#FEF3C7] text-sm font-semibold px-4 py-2.5 transition-colors"
          >
            <Zap size={15} /> Buy
          </button>
        </div>
      </div>
    </div>
  );
};
